// Face ID / biometric app-unlock seam ('s third acceptance clause). The Face ID gate
// "wraps TokenProvider unlock": on native a HELD session stays locked behind a successful biometric
// (or device-credential) auth before the app reveals authed content. Follows the `registerPlugin` seam pattern
// (status-bar / nfc / live-activity) so @cntrl/native needs no extra npm dep — the real plugin
// (`@aparajita/capacitor-biometric-auth`, registered name "BiometricAuth", the AC-only set) is added to
// the native project by / the native team (device-gated, the CoreNFC-write precedent). Web, or a native
// build where the plugin isn't registered yet, → no biometric, no gate.
//
// FAIL-OPEN on the ENTRY gate is deliberate and correct: a device with NO enrolled biometric AND no device
// passcode cannot authenticate, so requiring it would BRICK the app; the session is still protected by the
// Keychain-at-rest + the server session TTL, and the gate only ever engages when the OS reports an available
// factor ({@link biometricUnlockAvailable} false ⇒ the caller skips the gate).
import { Capacitor, registerPlugin } from "@capacitor/core";

/**
 * The subset of `@aparajita/capacitor-biometric-auth`'s contract this seam uses. `authenticate` RESOLVES on a
 * successful auth and REJECTS (a BiometryError) on cancel / lockout / failure — mapped to a boolean below so
 * callers never handle the throw. `checkBiometry` reports whether a factor is enrolled and usable right now.
 */
interface BiometricAuthPlugin {
  checkBiometry(): Promise<{ isAvailable: boolean }>;
  authenticate(options: {
    reason: string;
    cancelTitle?: string;
    /** Let a device without Face ID fall back to the passcode (standard app-lock parity). */
    allowDeviceCredential?: boolean;
    iosFallbackTitle?: string;
  }): Promise<void>;
}

const BiometricAuth = registerPlugin<BiometricAuthPlugin>("BiometricAuth");

/** The plugin is registered on this build (a native platform with the BiometricAuth plugin present). */
function pluginPresent(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("BiometricAuth");
}

/**
 * True iff a biometric (or device-credential) unlock CAN be performed on this device right now: native, the
 * plugin registered, and the OS reports an enrolled, usable factor. Web / no-plugin / no-enrolled-factor →
 * false, so the caller SKIPS the gate (never locks a device that could never unlock). Never throws.
 */
export async function biometricUnlockAvailable(): Promise<boolean> {
  if (!pluginPresent()) return false;
  try {
    const { isAvailable } = await BiometricAuth.checkBiometry();
    return isAvailable === true;
  } catch {
    return false;
  }
}

/**
 * Prompt a biometric unlock. Resolves `true` on success, `false` on cancel / failure / unavailable — never
 * throws, so the caller keeps the app LOCKED on `false` and offers a retry. `allowDeviceCredential` lets a
 * device without Face ID fall back to its passcode (parity with a standard app lock).
 */
export async function biometricUnlock(reason: string): Promise<boolean> {
  if (!pluginPresent()) return false;
  try {
    await BiometricAuth.authenticate({
      reason,
      allowDeviceCredential: true,
      cancelTitle: "Cancel",
      iosFallbackTitle: "Use Passcode",
    });
    return true;
  } catch {
    return false;
  }
}
